import * as PIXI from 'pixi.js';
import { createFlameMesh, type FlameSkin } from './fireShader';

/**
 * A placed torch: wooden haft, pitch-soaked rag head, the shared fire shader
 * for the flame, a flickering halo and ground pool, and rising embers.
 *
 * Shared by the iso and top-down token renderers so a torch looks the same in
 * both views. The container's origin is the token's centre; the haft meets
 * the ground TORCH_FOOT * r below it and the flame rises about 2.5r above.
 */

/**
 * Soft white radial falloff, tinted per torch and drawn additively. One
 * 128px texture serves every torch's halo and ground pool (sprites scale it),
 * so a room full of torches costs one upload.
 */
let glowTexture: PIXI.Texture | null = null;

function getGlowTexture(): PIXI.Texture {
  if (glowTexture) return glowTexture;
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return PIXI.Texture.WHITE;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.45)');
  g.addColorStop(0.6, 'rgba(255,255,255,0.12)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  glowTexture = PIXI.Texture.from(canvas);
  return glowTexture;
}

/** Sparks lifting off a torch. Positions are derived from phase, so no state. */
const EMBER_COUNT = 7;

/** Torch layout, in multiples of tokenRadius (negative = up). */
const TORCH_FOOT = 0.95;        // where the haft meets the ground
const TORCH_HEAD_BOTTOM = -0.42;
const TORCH_FLAME_BASE = -0.98; // top of the rag head, where the flame sits

/** Phase advance per ms; matches the iso renderer's idle bob speed. */
const TORCH_SPEED = 0.002;

export class TorchVisual {
  readonly container: PIXI.Container;

  private body: PIXI.Graphics;
  private flameGraphic: PIXI.Graphics | null = null;
  /** Firelit top lip of the rag wrap, drawn over the flame's base so it sits IN the head. */
  private torchFront: PIXI.Graphics;
  /** Additive halo around the flame and warm pool on the ground; both flicker. */
  private torchHalo: PIXI.Sprite;
  private torchPool: PIXI.Sprite;
  private embers: PIXI.Graphics;
  private fireShader: PIXI.Shader | null = null;
  private phase = Math.random() * Math.PI * 2; // so neighbouring torches don't flicker in sync

  /**
   * @param r - size unit in pixels (the token radius); the torch is about 3.5r tall
   * @param skin - flame colours (see getTorchSkin)
   */
  constructor(private readonly r: number, private readonly skin: FlameSkin) {
    this.container = new PIXI.Container();
    this.container.sortableChildren = true;
    this.container.eventMode = 'none';

    this.body = new PIXI.Graphics();
    this.body.zIndex = 2;
    this.container.addChild(this.body);

    const flameBaseY = r * TORCH_FLAME_BASE;

    // Warm pool on the ground: what makes a torch read as a light source
    // from across the map, before you can make out the flame itself.
    this.torchPool = new PIXI.Sprite(getGlowTexture());
    this.torchPool.anchor.set(0.5, 0.5);
    this.torchPool.tint = this.skin.glowColor;
    this.torchPool.blendMode = PIXI.BLEND_MODES.ADD;
    this.torchPool.width = r * 4.2;
    this.torchPool.height = r * 1.7;
    this.torchPool.position.set(0, r * 0.95);
    this.torchPool.alpha = 0.35;
    this.torchPool.zIndex = 0.5;
    this.torchPool.eventMode = 'none';
    this.container.addChild(this.torchPool);

    // Halo behind the flame
    this.torchHalo = new PIXI.Sprite(getGlowTexture());
    this.torchHalo.anchor.set(0.5, 0.5);
    this.torchHalo.tint = this.skin.glowColor;
    this.torchHalo.blendMode = PIXI.BLEND_MODES.ADD;
    this.torchHalo.width = this.torchHalo.height = r * 2.6;
    this.torchHalo.position.set(0, flameBaseY - r * 0.55);
    this.torchHalo.alpha = 0.45;
    this.torchHalo.zIndex = 1.5;
    this.torchHalo.eventMode = 'none';
    this.container.addChild(this.torchHalo);

    try {
      const { mesh, shader } = createFlameMesh(r * 1.45, 1.0, this.skin);
      // Flame rises out of the rag head, its base tucked under the front
      // lip. The shader's teardrop is slim; widen it so it hugs the bundle
      // like burning pitch rather than floating above it like a candle.
      mesh.position.set(0, flameBaseY + r * 0.16);
      mesh.scale.x = 1.45;
      mesh.zIndex = 3;
      this.container.addChild(mesh);
      this.fireShader = shader;
    } catch (e) {
      // WebGL shader may fail — fall back to Graphics flame
      console.warn('[Torch] Fire shader failed, using fallback:', e);
      this.flameGraphic = new PIXI.Graphics();
      this.flameGraphic.zIndex = 3;
      this.container.addChild(this.flameGraphic);
    }

    this.torchFront = new PIXI.Graphics();
    this.torchFront.zIndex = 4;
    this.torchFront.eventMode = 'none'; // don't eat pointer events
    this.container.addChild(this.torchFront);

    this.embers = new PIXI.Graphics();
    this.embers.blendMode = PIXI.BLEND_MODES.ADD;
    this.embers.zIndex = 5;
    this.embers.eventMode = 'none';
    this.container.addChild(this.embers);

    this.drawLightObject();
    this.update(0);
  }

  /** Advance the flame, flicker and embers. Call every frame with elapsed ms. */
  update(elapsedMs: number): void {
    this.phase += TORCH_SPEED * elapsedMs;
    const p = this.phase;
    if (this.fireShader) {
      this.fireShader.uniforms.uTime = p * 0.5; // slower = more majestic
    } else {
      this.drawFlame(p);
    }
    // Light flickers on three incommensurate beats so it never visibly loops
    const flicker = 1 + 0.08 * Math.sin(p * 7.1) + 0.05 * Math.sin(p * 12.7) + 0.03 * Math.sin(p * 23.3);
    this.torchHalo.alpha = 0.42 * flicker;
    this.torchPool.alpha = 0.32 * flicker;
    this.drawTorchFront(p);
    this.drawEmbers(p);
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }

  /**
   * Draw a placed torch: a wooden haft planted in the ground with a
   * pitch-soaked rag bundle at the top. The flame (shader or fallback) sits on
   * the bundle, and drawTorchFront() lays the firelit front lip over its base.
   */
  private drawLightObject(): void {
    const g = this.body;
    g.clear();
    const r = this.r;
    const skin = this.skin;

    const foot = r * TORCH_FOOT;
    const headBottom = r * TORCH_HEAD_BOTTOM;
    const headTop = r * TORCH_FLAME_BASE;

    g.lineStyle(0);

    // Scuffed earth where the haft is driven in
    g.beginFill(0x000000, 0.35);
    g.drawEllipse(0, foot, r * 0.2, r * 0.07);
    g.endFill();

    // ─── Haft: tapered wood, lit from the left ──────────
    const footHalf = r * 0.085;
    const neckHalf = r * 0.065;
    g.beginFill(skin.stickColor, 1);
    g.moveTo(-footHalf, foot);
    g.lineTo(-neckHalf, headBottom);
    g.lineTo(neckHalf, headBottom);
    g.lineTo(footHalf, foot);
    g.closePath();
    g.endFill();

    // Shadow side and highlight so it reads round, not flat
    g.beginFill(0x000000, 0.35);
    g.moveTo(footHalf * 0.2, foot);
    g.lineTo(neckHalf * 0.2, headBottom);
    g.lineTo(neckHalf, headBottom);
    g.lineTo(footHalf, foot);
    g.closePath();
    g.endFill();
    g.beginFill(skin.glowColor, 0.22);
    g.moveTo(-footHalf * 0.75, foot);
    g.lineTo(-neckHalf * 0.75, headBottom);
    g.lineTo(-neckHalf * 0.35, headBottom);
    g.lineTo(-footHalf * 0.35, foot);
    g.closePath();
    g.endFill();

    // Iron ferrule binding the head to the haft
    g.beginFill(skin.bracketColor, 1);
    g.drawRoundedRect(-neckHalf * 1.45, headBottom - r * 0.02, neckHalf * 2.9, r * 0.09, r * 0.02);
    g.endFill();
    g.beginFill(0xffffff, 0.18);
    g.drawRect(-neckHalf * 1.3, headBottom - r * 0.01, neckHalf * 1.1, r * 0.025);
    g.endFill();

    // ─── Head: rag bundle, swollen in the middle ────────
    const headHalf = r * 0.16;
    const headMid = (headBottom + headTop) / 2;
    g.beginFill(0x2a1c12, 1);
    g.moveTo(-neckHalf * 1.2, headBottom);
    g.bezierCurveTo(-headHalf * 1.15, headBottom - r * 0.08, -headHalf * 1.2, headMid, -headHalf, headTop + r * 0.05);
    g.quadraticCurveTo(0, headTop - r * 0.05, headHalf, headTop + r * 0.05);
    g.bezierCurveTo(headHalf * 1.2, headMid, headHalf * 1.15, headBottom - r * 0.08, neckHalf * 1.2, headBottom);
    g.closePath();
    g.endFill();

    // Diagonal wraps of cloth, lighter where the fire catches them
    const wraps = 4;
    for (let i = 0; i < wraps; i++) {
      const t = (i + 0.5) / wraps;
      const y = headBottom + (headTop - headBottom) * t;
      const half = headHalf * (0.85 + 0.25 * Math.sin(Math.PI * t));
      g.beginFill(0x5a3d26, 0.55 + t * 0.35);
      g.moveTo(-half, y + r * 0.035);
      g.lineTo(half, y - r * 0.03);
      g.lineTo(half, y - r * 0.065);
      g.lineTo(-half, y);
      g.closePath();
      g.endFill();
    }

    // Upper half of the bundle glows from the heat above it
    g.beginFill(skin.outerColor, 0.18);
    g.drawEllipse(0, headTop + r * 0.12, headHalf * 0.95, r * 0.12);
    g.endFill();

  }

  /** Charred front lip of the rag head with smouldering edge, over the flame's base. */
  private drawTorchFront(phase: number): void {
    if (!this.torchFront) return;
    const g = this.torchFront;
    g.clear();

    const r = this.r;
    const skin = this.skin;
    const headTop = r * TORCH_FLAME_BASE;
    const half = r * 0.16;

    g.beginFill(0x1e140d, 1);
    g.moveTo(-half, headTop + r * 0.05);
    g.quadraticCurveTo(0, headTop + r * 0.13, half, headTop + r * 0.05);
    g.lineTo(half * 0.95, headTop + r * 0.15);
    g.quadraticCurveTo(0, headTop + r * 0.22, -half * 0.95, headTop + r * 0.15);
    g.closePath();
    g.endFill();

    // Smouldering rim: breathes slightly out of step with the flame
    const glow = 0.55 + 0.25 * Math.sin(phase * 5.3) + 0.1 * Math.sin(phase * 13.1);
    g.lineStyle(r * 0.03, skin.innerColor, glow);
    g.moveTo(-half * 0.9, headTop + r * 0.06);
    g.quadraticCurveTo(0, headTop + r * 0.14, half * 0.9, headTop + r * 0.06);
    g.lineStyle(0);
  }

  /**
   * Sparks rising from the flame and drifting on the heat. Each ember's
   * position is a pure function of phase, so nothing is allocated per frame.
   */
  private drawEmbers(phase: number): void {
    if (!this.embers) return;
    const g = this.embers;
    g.clear();

    const r = this.r;
    const skin = this.skin;
    const baseY = r * TORCH_FLAME_BASE - r * 0.5;
    const rise = r * 1.6;

    for (let i = 0; i < EMBER_COUNT; i++) {
      const seed = i * 1.618;
      const life = (phase * (0.22 + (i % 3) * 0.05) + seed) % 1;
      const y = baseY - life * rise;
      const x = Math.sin(phase * 1.7 + seed * 3) * r * 0.12 * (0.4 + life) + Math.sin(seed * 7) * r * 0.08;
      const alpha = Math.sin(Math.PI * life) * 0.9;
      const size = r * (0.022 + 0.018 * (1 - life));
      g.beginFill(life < 0.5 ? skin.innerColor : skin.outerColor, alpha);
      g.drawCircle(x, y, size);
      g.endFill();
    }
  }

  /**
   * Draw animated pear-shaped flame with licking tongues.
   * Layered: outer glow → outer flame (pear) → licking tongues → inner flame → core.
   * Called from applyBob for per-frame animation.
   */
  private drawFlame(phase: number): void {
    if (!this.flameGraphic) return;
    const g = this.flameGraphic;
    g.clear();

    const r = this.r * 0.95;
    const skin = this.skin;
    // Flame sits on the rag head (going UP = negative Y)
    const baseY = this.r * TORCH_FLAME_BASE;

    // Flickering — multiple frequencies for organic feel
    const s1 = Math.sin(phase * 7) * 0.10;
    const s2 = Math.sin(phase * 11) * 0.07;
    const s3 = Math.sin(phase * 17) * 0.04;
    const scale = 1 + s1 + s3;
    const lean = (s2 + s3) * 4;

    // ─── Layer 1: Outer glow (soft, large teardrop) ────────
    // Teardrop: narrow at base, expands to belly ~28% up, tapers to tip
    g.beginFill(skin.glowColor, 0.12);
    g.moveTo(-r * 0.12 + lean, baseY);                    // narrow base
    g.bezierCurveTo(
      -r * 0.6 * scale + lean, baseY - r * 0.12 * scale,  // rapid expansion near base
      -r * 0.62 * scale + lean, baseY - r * 0.4 * scale,   // widest belly (lower)
      lean, baseY - r * 1.35 * scale,                      // tapers to tip
    );
    g.bezierCurveTo(
      r * 0.62 * scale + lean, baseY - r * 0.4 * scale,
      r * 0.6 * scale + lean, baseY - r * 0.12 * scale,
      r * 0.12 + lean, baseY,
    );
    g.closePath();
    g.endFill();

    // ─── Layer 2: Outer flame — teardrop body (red/orange) ─
    // Narrow at base, widest ~28% up, long taper to pointed tip
    g.beginFill(skin.outerColor, 0.85);
    const baseHalf = r * 0.1 * scale; // narrow at fuel point
    g.moveTo(-baseHalf + lean, baseY);
    g.bezierCurveTo(
      -r * 0.45 * scale + lean, baseY - r * 0.1 * scale,   // rapid expansion near base
      -r * 0.48 * scale + lean, baseY - r * 0.32 * scale,   // widest belly (lower)
      -r * 0.18 * scale + lean, baseY - r * 0.85 * scale,   // narrowing toward tip
    );
    g.quadraticCurveTo(
      lean, baseY - r * 1.2 * scale,                        // sharp pointed tip
      r * 0.18 * scale + lean, baseY - r * 0.85 * scale,
    );
    g.bezierCurveTo(
      r * 0.48 * scale + lean, baseY - r * 0.32 * scale,
      r * 0.45 * scale + lean, baseY - r * 0.1 * scale,
      baseHalf + lean, baseY,
    );
    g.closePath();
    g.endFill();

    // ─── Layer 3: Licking tongues — wisps from the belly ──
    // Left tongue — emerges from the widest part (now lower)
    const lt = Math.sin(phase * 13) * 0.12;
    const ltLean = lean - r * 0.15 + Math.sin(phase * 19) * 2;
    g.beginFill(skin.outerColor, 0.55);
    g.moveTo(ltLean, baseY - r * 0.4 * scale);
    g.bezierCurveTo(
      ltLean - r * 0.12, baseY - r * (0.7 + lt) * scale,
      ltLean - r * 0.05, baseY - r * (0.9 + lt) * scale,
      ltLean - r * 0.01, baseY - r * (1.1 + lt) * scale,
    );
    g.bezierCurveTo(
      ltLean + r * 0.03, baseY - r * (0.9 + lt) * scale,
      ltLean + r * 0.06, baseY - r * 0.7 * scale,
      ltLean + r * 0.03, baseY - r * 0.4 * scale,
    );
    g.closePath();
    g.endFill();

    // Right tongue
    const rt = Math.sin(phase * 15 + 1.5) * 0.1;
    const rtLean = lean + r * 0.12 + Math.sin(phase * 21) * 2;
    g.beginFill(skin.outerColor, 0.5);
    g.moveTo(rtLean, baseY - r * 0.35 * scale);
    g.bezierCurveTo(
      rtLean + r * 0.1, baseY - r * (0.65 + rt) * scale,
      rtLean + r * 0.04, baseY - r * (0.85 + rt) * scale,
      rtLean + r * 0.01, baseY - r * (1.0 + rt) * scale,
    );
    g.bezierCurveTo(
      rtLean - r * 0.02, baseY - r * (0.85 + rt) * scale,
      rtLean - r * 0.06, baseY - r * 0.65 * scale,
      rtLean - r * 0.03, baseY - r * 0.35 * scale,
    );
    g.closePath();
    g.endFill();

    // Center tongue (tallest, thinnest — from the upper taper)
    const ct = Math.sin(phase * 9 + 0.7) * 0.08;
    g.beginFill(skin.innerColor, 0.45);
    g.moveTo(lean - r * 0.03, baseY - r * 0.75 * scale);
    g.bezierCurveTo(
      lean - r * 0.04, baseY - r * (1.0 + ct) * scale,
      lean - r * 0.01, baseY - r * (1.2 + ct) * scale,
      lean, baseY - r * (1.35 + ct) * scale,
    );
    g.bezierCurveTo(
      lean + r * 0.01, baseY - r * (1.2 + ct) * scale,
      lean + r * 0.04, baseY - r * (1.0 + ct) * scale,
      lean + r * 0.03, baseY - r * 0.75 * scale,
    );
    g.closePath();
    g.endFill();

    // ─── Layer 4: Inner flame — smaller teardrop (yellow) ──
    // Same teardrop: narrow base, belly lower, pointed tip
    g.beginFill(skin.innerColor, 0.9);
    const ibh = r * 0.06 * scale;
    g.moveTo(-ibh + lean, baseY);
    g.bezierCurveTo(
      -r * 0.26 * scale + lean, baseY - r * 0.08 * scale,   // expand fast near base
      -r * 0.28 * scale + lean, baseY - r * 0.25 * scale,   // belly lower
      -r * 0.12 * scale + lean, baseY - r * 0.55 * scale,
    );
    g.quadraticCurveTo(
      lean, baseY - r * 0.72 * scale,
      r * 0.12 * scale + lean, baseY - r * 0.55 * scale,
    );
    g.bezierCurveTo(
      r * 0.28 * scale + lean, baseY - r * 0.25 * scale,
      r * 0.26 * scale + lean, baseY - r * 0.08 * scale,
      ibh + lean, baseY,
    );
    g.closePath();
    g.endFill();

    // ─── Layer 5: White-hot core at base ──────────────────
    // Tiny bright point right at the fuel contact
    g.beginFill(skin.coreColor, 0.75);
    g.moveTo(-r * 0.04 + lean, baseY);
    g.bezierCurveTo(
      -r * 0.08 + lean, baseY - r * 0.06,
      lean - r * 0.04, baseY - r * 0.15,
      lean, baseY - r * 0.2,
    );
    g.bezierCurveTo(
      lean + r * 0.04, baseY - r * 0.15,
      r * 0.08 + lean, baseY - r * 0.06,
      r * 0.04 + lean, baseY,
    );
    g.closePath();
    g.endFill();
  }
}
